import _ from 'lodash'
import Image from 'next/image'
import {FC} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {countryCards} from '@/content/countryCards'
import {OfficeCountry, useOfficeLocationsContent} from '@/content/officeLocations'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {alpha2ToName} from '@/utils/countries/translations'
import {useLanguage} from '@/utils/i18n/language'
import {TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {OfficeLocationButton} from './OfficeLocationButton'

type OfficeLocationCardProps = {
  country: OfficeCountry
  setActiveOffice?: (index: number) => void
}

export const OfficeLocationCard: FC<OfficeLocationCardProps> = ({country, setActiveOffice}) => {
  const language = useLanguage()
  const {t} = useTranslation('page')

  const {offices, ofText, previousAriaLabel, nextAriaLabel} = useOfficeLocationsContent()

  const office = offices[country]
  const officeIndex = Object.keys(offices).findIndex((office) => office === country)
  const numOfOffices = _.size(offices)

  return (
    <>
      <div className="wrapper">
        <div className="flag">
          <Image
            src={countryCards[country].imageSrc}
            alt={countryCards[country].imageAlt}
            style={{objectFit: 'contain'}}
            width={48}
            height={48}
          />
        </div>
        <div className="address">
          <div className="country">
            <Text fontWeight={TextFontWeight.bold}>{alpha2ToName(country, language)}</Text>
            <Text variant={TextVariant.text14} fontWeight={TextFontWeight.medium}>
              {officeIndex + 1} {t(ofText)} {numOfOffices}
            </Text>
          </div>
          <div className="street">
            <Text variant={TextVariant.text14} color={theme.palette.text.light}>
              {t(office?.street)}
            </Text>
          </div>
          <Text variant={TextVariant.text14} color={theme.palette.text.light}>
            {t(office?.city)}
          </Text>
        </div>
        {setActiveOffice && (
          <div className="buttons">
            <OfficeLocationButton
              variant="left"
              ariaLabel={t(previousAriaLabel)}
              onClick={() => setActiveOffice((officeIndex + numOfOffices - 1) % numOfOffices)}
            />
            <OfficeLocationButton
              ariaLabel={t(nextAriaLabel)}
              onClick={() => setActiveOffice((officeIndex + 1) % numOfOffices)}
            />
          </div>
        )}
      </div>

      <style jsx>{`
        .wrapper {
          width: 250px;
          padding: ${theme.spacing[4]};
          border-radius: 12px;
          background-color: ${theme.palette.background.default};
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
        }

        .flag {
          position: relative;
          width: 48px;
          height: 48px;
        }

        .address {
          margin-top: ${theme.spacing[4]};
        }

        .country {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .street {
          margin-top: ${theme.spacing[1]};
        }

        .buttons {
          display: flex;
          justify-content: flex-end;
          margin-top: ${theme.spacing[3]};
          gap: ${theme.spacing[2]};
        }

        @media ${TABLET_SCREENSIZE} {
          .wrapper {
            width: 100%;
            padding: ${theme.spacing[3]};
          }

          .address {
            margin-top: ${theme.spacing[3]};
          }

          .buttons {
            display: none;
          }
        }
      `}</style>
    </>
  )
}
